import React from 'react';
import { Image, Linking, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { setLabel, useDeal, useLabel } from '../../src/data';
import { averageLine, mallSearchUrl, marketLine, price, shipText, sourceNames, specText, timeAgo, won } from '../../src/format';
import { Action, Label, Panel, Tag } from '../../src/ui';
import { D } from '../../src/theme';
import { LABELS, type Deal } from '../../src/types';

/**
 * 딜 상세 — 홈과 같은 문법.
 *
 * 점수를 크게 쓰되 그 옆에 "무엇을 확인했는지" 를 붙인다. 채점표는 만점이 항목마다 다르므로
 * 막대는 항목별 비율이고, 미확인 항목은 숨기지 않고 비워 둔다. 모르는 걸 감추면 100점이
 * "좋은 딜" 이 아니라 "비교를 못 했다" 는 뜻이 되는데 사용자는 반대로 읽는다.
 */
const WEIGHTS: Record<string, number> = { market: 35, history: 15, velocity: 20, trust: 15, freshness: 15 };
const NAMES: Record<string, string> = { market: '시장가 대비', history: '가격 이력', velocity: '커뮤니티 반응', trust: '판매처 신뢰', freshness: '신선도' };
const PENALTY_NAMES: Record<string, string> = {
  representative: '대표가 의심', option_mismatch: '옵션 불일치', conditional: '조건부 가격', foreign: '해외 결제', abnormal_low: '비정상 저가',
};

// market_mall 은 "다나와 최저가(4몰)" 처럼 이미 '최저가' 를 담고 올 때가 있다
const marketSource = (mall: string | null) => (mall ? (mall.includes('최저가') ? mall : `${mall} 최저가`) : '다나와 최저가');

/** 시장가를 아는 딜만 할인율을 돌려준다. 모르면 화면에 자리를 만들지 않는다. */
function discount(d: Deal): number | null {
  if (!d.market_price || !d.effective_price) return null;
  const pct = Math.round((1 - d.effective_price / d.market_price) * 100);
  return pct >= 5 ? pct : null;
}

export default function DealScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const deal = useDeal(id);
  const label = useLabel(id);

  if (deal === undefined) return <Center text="불러오는 중" />;
  if (deal === null) return <Center text="이 딜은 더 이상 없어요" back={() => router.back()} />;
  return <Body deal={deal} label={label} top={insets.top} bottom={insets.bottom} onBack={() => (router.canGoBack() ? router.back() : router.replace('/'))} />;
}

function Center({ text, back }: { text: string; back?: () => void }) {
  return (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', gap: 14, backgroundColor: D.ground }}>
      <Text style={{ fontSize: 15, fontWeight: '700', color: D.ink2 }}>{text}</Text>
      {back ? (
        <Pressable onPress={back} style={s.ghost}>
          <Text style={s.ghostText}>돌아가기</Text>
        </Pressable>
      ) : null}
    </View>
  );
}

function Body({ deal, label, top, bottom, onBack }: { deal: Deal; label?: string; top: number; bottom: number; onBack: () => void }) {
  const [broken, setBroken] = React.useState(false);
  const thumb = !broken ? deal.thumb_url : null;
  const m = marketLine(deal);
  const cp = deal.coupon;
  const off = discount(deal);
  const comps = Object.entries(WEIGHTS).map(([k, w]) => ({ k, w, v: deal.components?.[k] ?? null }));
  const known = comps.filter((c) => c.v != null);
  const pens = Object.entries(deal.penalties ?? {}).filter(([, v]) => v);
  // 구매 링크 우선순위: 우리 추적 링크 → 글에서 찾은 판매 페이지 → 다나와 상품 페이지 → 판매처 검색 → 커뮤니티 글
  const direct = deal.affiliate_url ?? deal.product_url ?? null;
  const danawa = direct ? null : deal.market_url ?? null;
  const search = direct || danawa ? null : mallSearchUrl(deal);
  const buy = direct ?? danawa ?? search?.url ?? deal.urls[0];
  const buyLabel = direct
    ? deal.affiliate_kind === 'search' ? `${deal.mall ?? '판매처'}에서 찾기` : '구매하러 가기'
    : danawa ? '다나와에서 최저가 보기'
    : search ? search.label : '커뮤니티 글 보기';
  const isCoupang = deal.mall === '쿠팡' || /coupang\.com/.test(buy ?? '');
  const community = deal.urls[0];

  return (
    <View style={{ flex: 1, backgroundColor: D.ground }}>
      <View style={[s.nav, { paddingTop: top + 6 }]}>
        <Pressable onPress={onBack} style={s.back} hitSlop={10}>
          <Text style={s.backText}>← 뒤로</Text>
        </Pressable>
        <Text style={s.navTitle}>딜 상세</Text>
      </View>

      <ScrollView contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 28, gap: 10 }}>
        {/* 사진이 있으면 크게. 없으면 점수판이 그 자리를 대신한다 — 홈 타일과 같은 규칙 */}
        <View style={s.shot}>
          {thumb ? (
            <>
              <Image source={{ uri: thumb }} style={{ width: '100%', height: '100%' }} resizeMode="cover" onError={() => setBroken(true)} />
              <View style={s.scoreTag}>
                <Text style={s.scoreTagText}>{Math.round(deal.score)}</Text>
              </View>
            </>
          ) : (
            <View style={s.noShot}>
              <Text style={s.scoreNum}>{Math.round(deal.score)}</Text>
              <Text style={s.scoreUnit}>점</Text>
            </View>
          )}
        </View>

        <Panel style={{ gap: 9 }}>
          <Text style={s.meta}>
            {deal.mall ?? '판매처 미확인'} · {sourceNames(deal.sources)}
            {deal.posted_at ? ` · ${timeAgo(deal.posted_at)}` : ''}
          </Text>
          <Text style={s.title}>{deal.product}</Text>
          <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <Text style={s.price}>{price(deal)}</Text>
            {off !== null ? <Text style={s.off}>-{off}%</Text> : null}
          </View>
          <Text style={s.market}>
            {deal.market_price ? `${marketSource(deal.market_mall)} ${won(deal.market_price)}보다 ${m.text.replace('시장가보다 ', '')}` : m.text}
          </Text>
          {averageLine(deal) ? <Text style={s.avg}>{averageLine(deal)}</Text> : null}
        </Panel>

        {cp && (cp.applied_price_krw || cp.steps.length > 0) ? (
          <Panel style={{ gap: 11 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <Label>쿠폰 적용법</Label>
              <Tag label={cp.scope ?? '대상 미확인'} tone={cp.scope === '누구나' ? 'ink' : 'quiet'} />
            </View>
            {cp.applied_price_krw ? (
              <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8 }}>
                <Text style={s.couponPrice}>{won(cp.applied_price_krw)}</Text>
                <Text style={s.small}>적용 후 · 근거 {cp.evidence ?? '본문'}</Text>
              </View>
            ) : null}
            {cp.steps.map((st, i) => (
              <View key={i} style={{ flexDirection: 'row', gap: 9 }}>
                <Text style={s.stepNo}>{i + 1}</Text>
                <Text style={s.step}>{st}</Text>
              </View>
            ))}
            {cp.conditions.length ? <Text style={s.small}>조건: {cp.conditions.join(', ')}</Text> : null}
          </Panel>
        ) : null}

        <Panel style={{ gap: 12 }}>
          <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', gap: 10 }}>
            <View style={{ gap: 2, flex: 1, minWidth: 0 }}>
              <Label>채점표</Label>
              <Text style={s.scoreLine}>
                <Text style={s.scoreBig}>{Math.round(deal.score)}</Text>
                <Text style={s.scoreSuffix}>점 · {known.length}/5개 항목 확인</Text>
              </Text>
            </View>
            <Tag label={`신뢰도 ${Math.round(deal.confidence * 100)}%`} tone={deal.confidence >= 0.7 ? 'ink' : 'quiet'} />
          </View>
          <View style={{ gap: 8 }}>
            {comps.map(({ k, w, v }) => (
              <View key={k} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                <Text style={[s.compName, v == null && { color: D.ink3 }]}>{NAMES[k]}</Text>
                <View style={s.bar}>{v != null ? <View style={[s.barFill, { width: `${Math.max(0, Math.min(100, (v / w) * 100))}%` }]} /> : null}</View>
                <Text style={[s.compVal, v == null && { color: D.ink3 }]}>{v == null ? '미확인' : `${Math.round(v)}/${w}`}</Text>
              </View>
            ))}
          </View>
          {known.length < comps.length ? (
            <Text style={s.small}>확인 못 한 항목이 있으면 만점을 주지 않아요. 100점은 전부 비교해 봤다는 뜻입니다.</Text>
          ) : null}
          {pens.length ? (
            <View style={{ flexDirection: 'row', gap: 5, flexWrap: 'wrap' }}>
              {pens.map(([k, v]) => (
                <Tag key={k} label={`${PENALTY_NAMES[k] ?? k} -${Math.round(v)}`} tone="alert" />
              ))}
            </View>
          ) : null}
          {deal.reasons.length ? <Text style={s.small}>{deal.reasons.join(' · ')}</Text> : null}
        </Panel>

        <Panel style={{ paddingVertical: 2 }}>
          <Row label="배송" value={shipText(deal)} />
          {specText(deal.specs) ? <Row label="스펙" value={specText(deal.specs)} /> : null}
          {deal.conditions.length ? <Row label="조건" value={deal.conditions.join(', ')} /> : null}
          <Row
            label="출처"
            value={`${sourceNames(deal.sources)}${deal.comments ? ` · 댓글 ${deal.comments}` : ''}${deal.votes ? ` · 추천 ${deal.votes}` : ''}`}
            last={!deal.titles[0]}
          />
          {deal.titles[0] ? <Row label="원문" value={deal.titles[0]} last /> : null}
        </Panel>

        <View style={s.warn}>
          <Text style={s.warnText}>커뮤니티 제보가예요. 판매 페이지에서 옵션과 결제 금액을 다시 확인하세요.</Text>
        </View>

        <Panel style={{ gap: 10 }}>
          <Label>{label ? `"${label}" 으로 표시했어요` : '이 딜, 어땠어요?'}</Label>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            {LABELS.map((l) => (
              <Pressable key={l} onPress={() => setLabel(deal, l).catch(() => {})} style={[s.labelBtn, label === l && s.labelOn]}>
                <Text style={[s.labelText, label === l && { color: D.volt }]}>{l}</Text>
              </Pressable>
            ))}
          </View>
          <Text style={s.small}>한 달 뒤 채점표를 고칠 때 이 표시를 씁니다. 오탐 = 딜이 아님, 낚시 = 옵션·조건 숨김.</Text>
        </Panel>
      </ScrollView>

      <View style={[s.bottom, { paddingBottom: bottom + 12 }]}>
        <Action label={buyLabel} onPress={() => buy && Linking.openURL(buy)} disabled={!buy} />
        {community && buy !== community ? (
          <Pressable onPress={() => Linking.openURL(community)}>
            <Text style={s.link}>커뮤니티 원문 보기</Text>
          </Pressable>
        ) : null}
        <Text style={s.disclosure}>
          {isCoupang ? '이 게시물은 쿠팡 파트너스 활동의 일환으로, 이에 따른 일정액의 수수료를 제공받습니다' : '이 게시물은 제휴마케팅이 포함된 광고로 커미션을 지급 받습니다'}
        </Text>
      </View>
    </View>
  );
}

function Row({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <View style={[s.row, !last && { borderBottomWidth: 1, borderBottomColor: D.line }]}>
      <Text style={s.rowLabel}>{label}</Text>
      <Text style={s.rowValue} numberOfLines={2}>
        {value}
      </Text>
    </View>
  );
}

const s = StyleSheet.create({
  nav: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 16, paddingBottom: 12 },
  back: { borderWidth: 1, borderColor: D.ink, backgroundColor: D.panel, paddingVertical: 5, paddingHorizontal: 10 },
  backText: { fontSize: 12, fontWeight: '900', color: D.ink },
  navTitle: { fontSize: 12, fontWeight: '800', color: D.ink2, letterSpacing: 0.4 },
  ghost: { borderWidth: 1, borderColor: D.ink, backgroundColor: D.panel, paddingVertical: 8, paddingHorizontal: 14 },
  ghostText: { fontSize: 13, fontWeight: '900', color: D.ink },

  shot: { aspectRatio: 16 / 10, backgroundColor: D.line, borderWidth: 1, borderColor: D.ink },
  noShot: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 3, backgroundColor: D.ink },
  scoreNum: { fontSize: 58, fontWeight: '900', color: D.volt, letterSpacing: -2.5, fontVariant: ['tabular-nums'] },
  scoreUnit: { fontSize: 15, fontWeight: '800', color: D.volt, marginTop: 20 },
  scoreTag: { position: 'absolute', top: 0, right: 0, backgroundColor: D.ink, paddingVertical: 5, paddingHorizontal: 10 },
  scoreTagText: { fontSize: 16, fontWeight: '900', color: D.volt, fontVariant: ['tabular-nums'], letterSpacing: -0.3 },

  meta: { fontSize: 10.5, fontWeight: '800', color: D.ink3, letterSpacing: 0.3 },
  title: { fontSize: 19, fontWeight: '800', lineHeight: 26, color: D.ink, letterSpacing: -0.4 },
  price: { fontSize: 30, fontWeight: '900', color: D.ink, letterSpacing: -1.2, fontVariant: ['tabular-nums'] },
  off: { fontSize: 22, fontWeight: '900', color: D.alert, letterSpacing: -0.8, fontVariant: ['tabular-nums'] },
  market: { fontSize: 13, fontWeight: '700', color: D.ink2, lineHeight: 19 },
  avg: { fontSize: 12, color: D.ink3 },

  couponPrice: { fontSize: 21, fontWeight: '900', color: D.ink, fontVariant: ['tabular-nums'], letterSpacing: -0.6 },
  stepNo: { width: 18, height: 18, backgroundColor: D.ink, color: D.volt, fontSize: 11, fontWeight: '900', textAlign: 'center', lineHeight: 18, overflow: 'hidden' },
  step: { flex: 1, fontSize: 13.5, lineHeight: 20, color: D.ink },

  scoreLine: { flexDirection: 'row', alignItems: 'baseline' },
  scoreBig: { fontSize: 30, fontWeight: '900', color: D.ink, letterSpacing: -1.2, fontVariant: ['tabular-nums'] },
  scoreSuffix: { fontSize: 12, fontWeight: '700', color: D.ink2 },
  compName: { width: 82, fontSize: 12, fontWeight: '700', color: D.ink2 },
  bar: { flex: 1, height: 8, backgroundColor: D.line, borderWidth: 1, borderColor: D.ink, overflow: 'hidden' },
  barFill: { height: '100%', backgroundColor: D.ink },
  compVal: { width: 46, textAlign: 'right', fontSize: 11, fontWeight: '800', color: D.ink2, fontVariant: ['tabular-nums'] },
  small: { fontSize: 11.5, color: D.ink2, lineHeight: 17 },

  row: { flexDirection: 'row', gap: 12, paddingVertical: 11 },
  rowLabel: { width: 52, fontSize: 12, fontWeight: '800', color: D.ink3 },
  rowValue: { flex: 1, fontSize: 13, color: D.ink, lineHeight: 19 },

  warn: { backgroundColor: D.volt, borderWidth: 1, borderColor: D.ink, paddingHorizontal: 13, paddingVertical: 11 },
  warnText: { fontSize: 12.5, fontWeight: '800', lineHeight: 18, color: D.ink },

  labelBtn: { flex: 1, paddingVertical: 10, borderWidth: 1, borderColor: D.ink, backgroundColor: D.panel, alignItems: 'center' },
  labelOn: { backgroundColor: D.ink },
  labelText: { fontSize: 13, fontWeight: '800', color: D.ink },

  bottom: { paddingHorizontal: 16, paddingTop: 12, backgroundColor: D.panel, borderTopWidth: 1, borderTopColor: D.ink, gap: 8 },
  link: { fontSize: 12, fontWeight: '800', color: D.ink, textAlign: 'center', textDecorationLine: 'underline' },
  disclosure: { fontSize: 10, color: D.ink3, textAlign: 'center', lineHeight: 15 },
});
